import { Box, BoxProps, chakra, ChakraProps, useColorModeValue } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable, type ContentEditableProps } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { useStableCallback } from '@main/shared/browser';
import { protectEscapedMarkdownPunctuation } from '@main/shared/utils';
import deepmerge from 'deepmerge';
import { $setSelection, EditorState, LexicalEditor } from 'lexical';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import AutoLinkPlugin from '../lexical/plugins/auto-link';
import { BlurEditorPlugin } from '../lexical/plugins/BlurEditorPlugin';
import { BreakListsPlugin } from '../lexical/plugins/BreakListsPlugin';
import { ControlledLexicalPlugin } from '../lexical/plugins/ControlledPlugin';
import { LabelFocusPlugin } from '../lexical/plugins/LabelFocusPlugin';
import { MarkdownPastePlugin } from '../lexical/plugins/markdown-paste-plugin';
import { UpdateConfigPlugin } from '../lexical/plugins/UpdateConfigPlugin';
import theme from '../lexical/themes/markdown';
import styles from '../lexical/themes/markdown/styles';
import { restoreEscapedMarkdownPunctuationInDocument } from './markdown-escapes';
import {
  MD_EXPORT_TRANSFORMERS,
  MD_NODES,
  MD_SHORTCUT_TRANSFORMERS,
  MD_TRANSFORMERS,
} from './transformers';

// Re-exported so existing callers keep importing the transformer config from
// the editor module. The definitions now live in the DOM-free ./transformers.
export { MD_NODES, MD_THEME, MD_TRANSFORMERS } from './transformers';

const normalizeMarkdownValue = (value: string) => (value.trim().length === 0 ? '' : value);

const $importMarkdown = (value: string) => {
  $convertFromMarkdownString(protectEscapedMarkdownPunctuation(value), MD_TRANSFORMERS);
  restoreEscapedMarkdownPunctuationInDocument();
};

export interface MarkdownEditorProps extends MarkdownPluginsProps {
  id?: string;
  config?: Partial<InitialConfigType>;
  placeholder?: ReactNode;
  /** Only what callers use: the placeholder's size, and an inset. */
  placeholderProps?: Pick<ChakraProps, 'fontSize' | 'px' | 'color'>;
  contentEditable?: ReactNode;
  variant?: 'unstyled' | 'textarea' | 'property';
  boxProps?: BoxProps;
}

const useVariantBoxProps = (
  variant: MarkdownEditorProps['variant'],
  isEditable?: boolean,
): Partial<BoxProps> => {
  const propertyHoverBgColor = useColorModeValue('gray.100', 'gray.600');
  const globalBoxStyles = {
    display: 'block',
  };
  const globalPlacerholderStyles = {
    top: 0,
  };
  const globalContentEditableStyles = {
    outline: 'none',
    fontSize: 'xs',
    lineHeight: 'var(--chakra-lineHeights-base)',
  };
  const commonStyledVariantBoxStyles = {
    borderRadius: 'md',
    transitionProperty: 'common',
    transitionDuration: 'normal',
    border: '1px',
  };
  switch (variant) {
    case 'textarea':
      /* The ComplyKit text entry's frame and type, so an editor beside a
       * TextArea reads as the same control until the editor round owns it. */
      return {
        ...globalBoxStyles,
        border: '1px solid var(--ck-border-default)',
        borderRadius: 'var(--ck-radius-3)',
        transitionProperty: 'color, background-color, border-color, box-shadow',
        transitionTimingFunction: 'var(--ck-motion-ease-standard)',
        transitionDuration: 'var(--ck-motion-duration-fast)',
        /* Colour, fill and hover are style props, not sx, so a caller's
         * boxProps still win (the questionnaire section description stays
         * muted and borderless); the dark well is a variable for the same
         * reason, a plain `bg` turns it off. */
        color: 'var(--ck-text-default)',
        bg: 'var(--markdown-entry-well, transparent)',
        _hover: { borderColor: 'var(--ck-border-active)' },
        sx: {
          _dark: { '--markdown-entry-well': 'var(--ck-neutral-a-2)' },
          '& > .editor-input': {
            /* Three lines at 14/20 plus the padding: the TextArea's md height. */
            minH: '4.75rem',
            py: 2,
            px: 3,
            outline: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          },
          '& > .editor-placeholder': {
            ...globalPlacerholderStyles,
            py: 2,
            px: 3,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            color: 'var(--ck-text-muted)',
          },
          '&:focus-within': {
            borderColor: 'var(--ck-accent-8)',
            boxShadow: '0 0 0 3px var(--ck-focus-halo)',
          },
          '&[data-invalid]': { borderColor: 'var(--ck-error-border)' },
          '&[data-invalid]:focus-within': { boxShadow: '0 0 0 3px var(--ck-error-halo)' },
        },
      };
    case 'property':
      return {
        ...globalBoxStyles,
        ...commonStyledVariantBoxStyles,
        borderColor: 'transparent',
        sx: {
          '& > .editor-input': {
            p: 2,
            ...globalContentEditableStyles,
          },
          '& > .editor-placeholder': {
            ...globalPlacerholderStyles,
            p: 2,
          },
        },
        _hover: isEditable ? { bg: propertyHoverBgColor, cursor: 'pointer' } : undefined,
        _focusWithin: {
          bg: 'none',
          cursor: 'auto',
          borderColor: 'gray.200',
          _dark: {
            borderColor: 'rgba(255,255,255,0.16)',
          },
        },
        w: 'full',
      };
    case 'unstyled':
    default:
      return {
        ...globalBoxStyles,
        sx: {
          '& > .editor-input': {
            ...globalContentEditableStyles,
          },
          '& > .editor-placeholder': {
            ...globalPlacerholderStyles,
          },
        },
      };
  }
};

export function MarkdownEditor(props: MarkdownEditorProps) {
  const defaultValue = props.defaultValue ?? '';
  const variant = props.variant ?? 'unstyled';
  const placeholderColor = useColorModeValue('gray.500', 'whiteAlpha.400');

  return (
    <LexicalComposer
      key={defaultValue}
      initialConfig={{
        namespace: `markdown-${props.id ?? 'editor'}`,
        editorState: () => {
          $importMarkdown(defaultValue);
          /**
           * Following line is required to reset selection and avoid editor focus
           * https://github.com/facebook/lexical/issues/2636#issuecomment-1184418601
           */
          $setSelection(null);
        },
        // eslint-disable-next-line no-console
        onError: console.error,
        nodes: MD_NODES,
        theme,
        ...props.config,
      }}
    >
      <Global styles={styles} />
      <Box
        className="editor-container"
        data-invalid={props.contentEditableProps?.['aria-invalid'] ? '' : undefined}
        minH="initial"
        {...deepmerge(useVariantBoxProps(variant, props.config?.editable), props.boxProps ?? {})}
      >
        <RichTextPlugin
          placeholder={
            <chakra.p
              className="editor-placeholder"
              color={placeholderColor}
              {...props.placeholderProps}
            >
              {props.placeholder}
            </chakra.p>
          }
          contentEditable={
            props.contentEditable ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>{props.contentEditable}</>
            ) : (
              <ContentEditable
                className="editor-input"
                {...props.contentEditableProps}
                aria-labelledby={
                  props.contentEditableProps?.ariaLabelledBy ??
                  props.contentEditableProps?.['aria-labelledby']
                }
              />
            )
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </Box>
      <MarkdownPlugins {...props} />
    </LexicalComposer>
  );
}

export interface MarkdownPluginsProps {
  value?: string;
  defaultValue?: string | null;
  contentEditableProps?: ContentEditableProps;
  onValueChange?(value: string, editor: LexicalEditor): void;
  onChange?(state: EditorState, editor: LexicalEditor): void;
  onBlur?(value: string, editor: LexicalEditor): void;
  config?: Partial<InitialConfigType>;
}

export function MarkdownPlugins({
  onValueChange: onValueChangeProp,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  ...props
}: MarkdownPluginsProps) {
  const isEditable = useLexicalEditable();
  const [prevValue, setPrevValue] = useState(() =>
    normalizeMarkdownValue(props.defaultValue ?? ''),
  );

  const updater = useCallback((value = '') => {
    $importMarkdown(value);
    /**
     * Following line is required to reset selection and avoid editor focus
     * https://github.com/facebook/lexical/issues/2636#issuecomment-1184418601
     */
    $setSelection(null);
  }, []);

  const onValueChange = useStableCallback(onValueChangeProp);
  const onChange = useStableCallback(onChangeProp);
  const onBlur = useStableCallback(onBlurProp);

  const handleOnChange: MarkdownPluginsProps['onChange'] = useMemo(
    () =>
      onValueChange || onChange
        ? (state, editor, ...args) => {
            if (onValueChange) {
              state.read(() => {
                const value = normalizeMarkdownValue(
                  $convertToMarkdownString(MD_EXPORT_TRANSFORMERS),
                );

                if (value === prevValue) {
                  return;
                }

                setPrevValue(value);
                onValueChange(value, editor);
              });
            }

            onChange?.(state, editor, ...args);
          }
        : undefined,
    [onValueChange, onChange, prevValue],
  );

  const handleOnBlur = useMemo(
    () =>
      onBlur
        ? (editor: LexicalEditor) =>
            editor
              .getEditorState()
              .read(() =>
                onBlur(
                  normalizeMarkdownValue($convertToMarkdownString(MD_EXPORT_TRANSFORMERS)),
                  editor,
                ),
              )
        : undefined,
    [onBlur],
  );

  const getCurrentValue = useStableCallback((editor: LexicalEditor) =>
    editor
      .getEditorState()
      .read(() => normalizeMarkdownValue($convertToMarkdownString(MD_EXPORT_TRANSFORMERS))),
  );

  return (
    <>
      <HistoryPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <MarkdownShortcutPlugin transformers={MD_SHORTCUT_TRANSFORMERS} />
      <MarkdownPastePlugin />
      <LinkPlugin validateUrl={() => true} />
      <AutoLinkPlugin />
      <TabIndentationPlugin />
      <TablePlugin />
      <BreakListsPlugin />
      <ControlledLexicalPlugin
        value={props.value}
        getCurrentValue={getCurrentValue}
        updater={updater}
      />
      <UpdateConfigPlugin config={props.config} />
      {props.contentEditableProps?.id && <LabelFocusPlugin id={props.contentEditableProps?.id} />}
      {handleOnChange && isEditable ? <OnChangePlugin onChange={handleOnChange} /> : ''}
      {handleOnBlur ? <BlurEditorPlugin onBlur={handleOnBlur} /> : ''}
    </>
  );
}
